import {Activity, lang} from '@mobi-components/global-solution';
import {startOfDay} from '@mobi-components/global-date-time-picker';

// Single date format: Sep 14, 2023
const formatDate = (date: Date) => {
  const formatOption: Intl.DateTimeFormatOptions = {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  };

  return date.toLocaleDateString('en-US', formatOption);
};

// Date Range formats:
//   Different years             Dec 30, 2023 - Jan 8, 2024
//   Same year, different monts  Apr 20 - May 12, 2023
//   Same year, same monts       Apr 20 - 28, 2023
const formatDateRange = (startDate?: Date, endDate?: Date) => {
  if (!startDate || !endDate) {
    return '';
  }

  const isSameMonth = startDate.getMonth() === endDate.getMonth();
  const isSameYear = startDate.getFullYear() === endDate.getFullYear();

  const formatOptionsStart: Intl.DateTimeFormatOptions = {
    month: 'short',
    day: 'numeric',
    year: isSameYear ? undefined : 'numeric',
  };

  const formatOptionsEnd: Intl.DateTimeFormatOptions = {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  };

  const formattedDateStart = startDate.toLocaleDateString('en-US', formatOptionsStart);

  // If same month & year we don't show month name: 24, 2023.
  // Using templates in this case, as toLocaleDateString() can't show date as number without month,
  // it returns: 'Day: 20'
  const formattedDateEnd =
    isSameMonth && isSameYear
      ? `${endDate.getDate()}, ${endDate.getFullYear()}`
      : endDate.toLocaleDateString('en-US', formatOptionsEnd);

  const formattedRange = `${formattedDateStart} - ${formattedDateEnd}`;
  return formattedRange;
};

// Format if only departure date is set: 'Apr 8'
// Same / different months format: 'Apr 7-14' / 'Apr 28 - Mar'
const formatShortDateRange = (startDate?: Date | null, endDate?: Date | null): string => {
  if (!startDate) {
    return 'No dates selected';
  }

  const getMonth = (date: Date) => `${date.toLocaleDateString('en-US', {month: 'short'})}`;

  // if only departure date is set: 'Apr 8'
  if (!endDate) {
    return `${getMonth(startDate)} ${startDate.getDate()}`;
  }

  // same / different months format: 'Apr 7-14' / 'Apr 28 - Mar'
  const shortDatesStr =
    startDate.getMonth() === endDate.getMonth()
      ? `${getMonth(startDate)} ${startDate.getDate()} - ${endDate.getDate()}`
      : `${getMonth(startDate)} ${startDate.getDate()} - ${getMonth(endDate)} ${endDate.getDate()}`;

  return shortDatesStr;
};

const getFlightDates = (flight: Activity) => {
  if (!flight) {
    return '';
  }
  const departureTime = new Date(flight.scheduled_start_time * 1000);
  const arrivalTime = new Date(flight.scheduled_end_time * 1000);
  const departureLocalTime = departureTime.toLocaleTimeString(lang(), {
    hour: 'numeric',
    minute: '2-digit',
    hour12: false,
  });
  const arrivalLocalTime = arrivalTime.toLocaleTimeString(lang(), {
    hour: 'numeric',
    minute: '2-digit',
    hour12: false,
  });
  const departureDate = startOfDay(departureTime);
  const arrivalDate = startOfDay(arrivalTime);
  const dateDiff = arrivalDate.valueOf() - departureDate.valueOf();
  const departureLocalDate = departureDate.toLocaleDateString(lang(), {month: 'short', day: 'numeric'});

  const diff = dateDiff > 0 ? `+${Math.floor(dateDiff / 24 / 3600 / 1000)}` : '';

  const hours = Math.floor(flight.duration / 3600);
  const minutes = Math.floor((flight.duration % 3600) / 60);
  const fixedMinutes = minutes.toString().length > 1 ? `${minutes.toString()}m` : `0${minutes.toString()}m`;
  const fixedHours = hours > 0 ? `${hours}h ` : '';

  return {
    departure: `${departureLocalDate}, ${departureLocalTime}`,
    name: flight.name.split(' ')[0],
    arrival: `${arrivalLocalTime}`,
    duration: `${fixedHours}${fixedMinutes}`,
    diff,
  };
};

export const DateFormatter = {
  formatDate,
  formatDateRange,
  formatShortDateRange,
  getFlightDates,
};
